import { controlPlayback, STARTED_EVENT, type Player } from "@scripts/playback";
import type { ComponentInit } from "@scripts/registry";

const EMBED = "[data-fa-hero-video-embed]";
const YOUTUBE_ID = "data-fa-hero-video-youtube";
const EMBED_SRC = "data-fa-hero-video-src";
const STARTED = "fa-hero-video-layer--started";

const SUBSCRIBE_TO_STATE = { event: "listening", id: 1, channel: "widget" };
const YOUTUBE_PLAYING = 1;

const command = (func: "playVideo" | "pauseVideo") => ({ event: "command", func, args: [] });

const youtubeEmbedUrl = (id: string) => {
	const encoded = encodeURIComponent(id);

	return `https://www.youtube-nocookie.com/embed/${encoded}?autoplay=1&mute=1&controls=0&loop=1&playlist=${encoded}&playsinline=1&modestbranding=1&rel=0&iv_load_policy=3&disablekb=1&fs=0&enablejsapi=1`;
};

const embedUrlOf = (embed: HTMLIFrameElement): string | undefined => {
	const id = embed.getAttribute(YOUTUBE_ID);

	return embed.getAttribute(EMBED_SRC) ?? (id ? youtubeEmbedUrl(id) : undefined);
};

const playerState = (data: unknown): unknown => {
	if (typeof data !== "string") return undefined;

	try {
		const { event, info } = JSON.parse(data) as { event?: string; info?: unknown };

		if (event === "onStateChange") return info;
		if (event === "infoDelivery" && typeof info === "object" && info !== null)
			return (info as { playerState?: unknown }).playerState;
	} catch {
		return undefined;
	}

	return undefined;
};

const youtubePlayer = (embed: HTMLIFrameElement, src: string, signal: AbortSignal): Player => {
	const origin = new URL(src).origin;
	const send = (message: object) =>
		embed.contentWindow?.postMessage(JSON.stringify(message), origin);

	embed.addEventListener("load", () => send(SUBSCRIBE_TO_STATE), { signal });

	return {
		play: () => {
			if (embed.hasAttribute("src")) send(command("playVideo"));
			else embed.src = src;
		},
		pause: () => {
			if (embed.hasAttribute("src")) send(command("pauseVideo"));
		},
		onPlaying: (callback) =>
			window.addEventListener(
				"message",
				(event) => {
					if (event.source !== embed.contentWindow || event.origin !== origin) return;
					if (playerState(event.data) === YOUTUBE_PLAYING) callback();
				},
				{ signal },
			),
		teardown: () => {
			if (!embed.hasAttribute("src")) return;

			/* Removing `src` alone leaves the loaded player running, so navigate it away first. */
			embed.src = "about:blank";
			embed.removeAttribute("src");
		},
	};
};

/** The `Video` primitive's own script drives an upload; this drives YouTube. Either way the layer
    fades in on the playback protocol's started event. */
export const initHeroVideo: ComponentInit = (layer) => {
	const listeners = new AbortController();
	const embed = layer.querySelector<HTMLIFrameElement>(EMBED);
	const embedSrc = embed ? embedUrlOf(embed) : undefined;
	const release =
		embed && embedSrc
			? controlPlayback(embed, (signal) => youtubePlayer(embed, embedSrc, signal))
			: undefined;

	layer.addEventListener(STARTED_EVENT, () => layer.classList.add(STARTED), {
		signal: listeners.signal,
	});

	return () => {
		listeners.abort();
		release?.();
		layer.classList.remove(STARTED);
	};
};
